import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Card } from 'tea-component';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import Filter from '@/pages/intelligence/components/Filter';
import {
  resolveDataSourceFilterValues,
  type TDataSourceFilterOption,
} from '../../../utils';

interface IMetricsFilterProps {
  filterConfirm: (value: Record<string, unknown>) => void;
  filterReset?: () => void;
  pageid?: string;
  metricValue?: string[];
  gameTypeList?: string[];
  dataSourceOptions?: TDataSourceFilterOption[];
}

export default function MetricsFilter(props: IMetricsFilterProps) {
  const {
    filterConfirm,
    filterReset,
    pageid,
    metricValue,
    gameTypeList,
    dataSourceOptions = [],
  } = props;

  const defaultValue = {
    [filterKeyMap.DATA_SOURCE_FULL]: { value: [] },
    [filterKeyMap.GAMETYPE_FULL]: { value: ['Mobile', 'PC', 'Console'] },
    [filterKeyMap.METRICS_FULL]: { value: [] },
    [filterKeyMap.QRANULARITY_FULL]: { value: ['Day', 'Week', 'Month', 'Quarter', 'All Time'] },
    [filterKeyMap.FREQ_FULL]: { value: ['Hour', 'Day', 'Week', 'Month', 'Quarter'] },
  };

  const { setFilterExtend, resetFilterExtend } = useFilterExtend({});

  const resolvedSourceValues = useMemo(
    () => resolveDataSourceFilterValues(pageid ? pageid.split(',') : [], dataSourceOptions),
    [pageid, dataSourceOptions],
  );

  const buildFormValue = useCallback(
    () => ({
      [filterKeyMap.DATA_SOURCE_FULL]: { value: resolvedSourceValues },
      [filterKeyMap.GAMETYPE_FULL]: {
        value: gameTypeList?.length ? gameTypeList : ['Mobile', 'PC', 'Console'],
      },
      [filterKeyMap.METRICS_FULL]: { value: metricValue || [] },
      [filterKeyMap.QRANULARITY_FULL]: { value: ['Day', 'Week', 'Month', 'Quarter', 'All Time'] },
      [filterKeyMap.FREQ_FULL]: { value: ['Hour', 'Day', 'Week', 'Month', 'Quarter'] },
    }),
    [resolvedSourceValues, gameTypeList, metricValue],
  );

  const [formValue, setFormValue] = useState(buildFormValue);
  const formValueRef = useRef(formValue);
  const urlFilterAppliedRef = useRef(false);

  const companyRef = useRef();
  const gametypeRef = useRef();
  const metricsRef = useRef();
  const granularityRef = useRef();
  const frequencyRef = useRef();

  const buildInputList = (options: TDataSourceFilterOption[]) => {
    const defaultInputList = {
      DATA_SOURCE_FULL: {
        key: filterKeyMap.DATA_SOURCE_FULL,
        default: defaultValue[filterKeyMap.DATA_SOURCE_FULL],
        ref: companyRef,
        options,
      },
      GAMETYPE_FULL: {
        key: filterKeyMap.GAMETYPE_FULL,
        default: defaultValue[filterKeyMap.GAMETYPE_FULL],
      },
      METRICS_FULL: {
        key: filterKeyMap.METRICS_FULL,
        default: defaultValue[filterKeyMap.METRICS_FULL],
        ref: metricsRef,
      },
      QRANULARITY_FULL: {
        key: filterKeyMap.QRANULARITY_FULL,
        default: defaultValue[filterKeyMap.QRANULARITY_FULL],
      },
      FREQ_FULL: {
        key: filterKeyMap.FREQ_FULL,
        default: defaultValue[filterKeyMap.FREQ_FULL],
      },
    };

    return [
      defaultInputList.DATA_SOURCE_FULL,
      defaultInputList.GAMETYPE_FULL,
      defaultInputList.METRICS_FULL,
      defaultInputList.QRANULARITY_FULL,
      defaultInputList.FREQ_FULL,
    ];
  };

  const [inputList, setInputList] = useState(() => buildInputList(dataSourceOptions));

  useEffect(() => {
    setInputList(buildInputList(dataSourceOptions));
  }, [dataSourceOptions]);

  useEffect(() => {
    urlFilterAppliedRef.current = false;
  }, [pageid]);

  useEffect(() => {
    const nextFormValue = buildFormValue();
    setFormValue(nextFormValue);
    formValueRef.current = nextFormValue;
  }, [buildFormValue]);

  useEffect(() => {
    if (!dataSourceOptions.length || !resolvedSourceValues.length || urlFilterAppliedRef.current) {
      return;
    }
    urlFilterAppliedRef.current = true;
    filterConfirm({
      source: resolvedSourceValues,
      game_type: gameTypeList?.length ? gameTypeList : ['Mobile', 'PC', 'Console'],
      metrics: metricValue || [],
      granularity: ['Day', 'Week', 'Month', 'Quarter', 'All Time'],
      frequency: ['Hour', 'Day', 'Week', 'Month', 'Quarter'],
    });
  }, [
    dataSourceOptions,
    resolvedSourceValues,
    gameTypeList,
    metricValue,
    filterConfirm,
  ]);

  const onConfirm = useCallback(
    (value /** 筛选器数据 */, apiValue /** 筛选器转换后的数据 */) => {
      formValueRef.current = value;
      setFormValue(value);
      const filterForm = {
        source: value[filterKeyMap.DATA_SOURCE_FULL]?.value,
        game_type: value[filterKeyMap.GAMETYPE_FULL]?.value,
        metrics: value[filterKeyMap.METRICS_FULL]?.value,
        granularity: value[filterKeyMap.QRANULARITY_FULL]?.value,
        frequency: value[filterKeyMap.FREQ_FULL]?.value,
      };
      filterConfirm(filterForm);
      setFilterExtend(apiValue);
    },
    [filterConfirm, setFilterExtend],
  );

  const onReset = useCallback(
    () => {
      formValueRef.current = {
        ...defaultValue,
      };
      companyRef?.current?.reset();
      gametypeRef?.current?.reset();
      metricsRef?.current?.reset();
      granularityRef?.current?.reset();
      frequencyRef?.current?.reset();
      resetFilterExtend();
      if (filterReset instanceof Function) {
        filterReset();
      }
    },
    [filterReset, resetFilterExtend],
  );

  const filterKey =
    dataSourceOptions.map(option => option.value).join('|') || 'empty';

  return (
    <Card>
      <Card.Body>
        <Filter
          key={filterKey}
          value={formValue}
          inputList={inputList}
          onConfirm={onConfirm}
          onReset={onReset}
          defaultValue={defaultValue}
        />
      </Card.Body>
    </Card>
  );
}
